import { SlideshowSelectEvent } from '@theme/events';

const WIDTHS = [352, 550, 832, 1200, 1600, 2048];
const PREFETCH_AHEAD = 2;

/**
 * Builds a srcset from the single URL the Liquid stub carries.
 * @param {string} src - A Shopify CDN URL containing a `width=` param.
 * @returns {string} The srcset attribute value.
 */
function buildSrcset(src) {
  return WIDTHS.map((width) => `${src.replace(/width=\d+/, `width=${width}`)} ${width}w`).join(', ');
}

/**
 * Replaces a slide's skeleton with a real image. No-op once hydrated.
 * @param {Element | undefined} slide - The slideshow slide to hydrate.
 */
function hydrate(slide) {
  const pending = slide?.querySelector('[data-media-src]');

  if (!(pending instanceof HTMLElement)) return;

  const { mediaSrc, mediaSizes, mediaFocal } = pending.dataset;

  if (!mediaSrc) return;

  pending.removeAttribute('data-media-src');

  const media = document.createElement('div');
  media.className = 'product-media';
  media.style.setProperty('--ratio', pending.style.getPropertyValue('--ratio'));

  const image = document.createElement('img');
  image.className = 'product-media__image';
  image.alt = '';
  image.decoding = 'async';
  image.sizes = mediaSizes ?? '100vw';
  if (mediaFocal) image.style.setProperty('--focal-point', mediaFocal);
  image.srcset = buildSrcset(mediaSrc);
  image.src = mediaSrc;

  const removeSkeleton = () => pending.remove();
  image.addEventListener('load', removeSkeleton, { once: true });
  image.addEventListener('error', removeSkeleton, { once: true });

  media.appendChild(image);
  pending.before(media);

  // A cached image can already be complete here, in which case no load event follows.
  if (image.complete) removeSkeleton();
}

/**
 * Hydrates the given slide and its neighbours, so a swipe lands on a loaded image.
 * @param {Element | null} slideshow - The slideshow component.
 * @param {number} index - The index to hydrate from.
 */
function hydrateFrom(slideshow, index) {
  const slides = /** @type {{ slides?: Element[] }} */ (slideshow)?.slides;

  if (!slides) return;

  for (let i = index - 1; i <= index + PREFETCH_AHEAD; i++) {
    hydrate(slides[i]);
  }
}

document.addEventListener(SlideshowSelectEvent.eventName, (event) => {
  const slideshow = event.target;

  if (!(slideshow instanceof Element) || !slideshow.closest('.card-gallery')) return;

  hydrateFrom(slideshow, /** @type {SlideshowSelectEvent} */ (event).detail.index);
});

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const gallery = event.target.closest('.card-gallery');

    if (!gallery) return;

    const slideshow = gallery.querySelector('slideshow-component');

    hydrateFrom(slideshow, /** @type {{ current?: number }} */ (slideshow)?.current ?? 0);
  },
  { passive: true, capture: true }
);
